// CORE
import React, { useRef, useState } from 'react';

import { CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Select, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { useRegisterOnAfterDate } from './useRegisterOnAfterDate';

type RegisterOnAfterDateProps = {
  label?: string;
  overrideText?: string;
  rootClassName?: string;
  /** Will the selection affect the global preference */
  temporarySelection?: boolean;
  /** Mixpanel prop for initiator */
  initiator?: string;
  controlled?: boolean;
  onChange?: (dateIso: string | null) => void;
};

const useStyles = makeStyles()((theme) => ({
  last: {
    '& div:hover': {
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
    backgroundColor: `${theme.palette.background.paper} !important`,
    borderRadius: 12,
    width: '100%',
  },
}));

function RegisterOnAfterDate({
  label,
  rootClassName,
  overrideText,
  temporarySelection,
  initiator,
  onChange,
  controlled = true,
}: RegisterOnAfterDateProps) {
  const { classes } = useStyles();
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  const {
    yearOnAfter,
    minDateYear,
    minDateISO,
    isLoading,
    years,
    value: val,
    selectYear,
  } = useRegisterOnAfterDate({
    temporarySelection,
    initiator,
    controlled,
    onChange,
  });

  const [initialVal] = useState(val);

  return yearOnAfter != null && minDateISO ? (
    <FormControl>
      <InputLabel id='select-label-after-date'>{label}</InputLabel>
      <Select
        id={'after_date_year_dropdown'}
        className={rootClassName}
        labelId='select-label-after-date'
        ref={ref}
        variant={'outlined'}
        label={label}
        size='small'
        IconComponent={isLoading ? () => null : undefined}
        name={'yearOnAfter'}
        value={controlled ? val : undefined}
        defaultValue={!controlled ? initialVal : undefined}
        onChange={(e) => {
          selectYear(e.target.value as number | 'All');
          ref.current?.blur();
        }}
        renderValue={(year) => overrideText || (year === 'All' ? `All Time (Since ${minDateYear})` : `Since ${year}`)}
        endAdornment={
          isLoading ? (
            <InputAdornment position='start' sx={{ position: 'absolute', right: 4 }}>
              <CircularProgress size={16} />
            </InputAdornment>
          ) : null
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: 200,
              paddingRight: 8,
            },
            sx: {
              '& .MuiList-root': {
                paddingBottom: 0,
              },
              '& .MuiMenuItem-root[data-value="All"] ': {
                position: 'sticky',
                borderRadius: 0,
                borderTop: `1px solid ${theme.palette.border.light}`,
                backgroundColor: theme.palette.background.paper,
                bottom: 0,
                height: 48,
                mt: 1,
                pt: 1,
                pb: 1,
                px: 0,
                '& div': {
                  width: '100%',
                  borderRadius: 2,
                  px: 2,
                },
              },
              '& .MuiMenuItem-root[data-value="All"]:hover,  & .Mui-selected': {
                backgroundColor: theme.palette.background.paper,
                pt: 1,
                pb: 1,
                '& div': {
                  backgroundColor: theme.palette.action.hover,
                  mt: 0,
                  py: 1,
                },
              },
            },
          },
        }}
      >
        {years.map((year) => (
          <MenuItem key={`target-year-${year}`} value={year}>
            {`Since ${year}`}
          </MenuItem>
        ))}
        <MenuItem key={'target-year-all'} value={'All'} className={classes.last}>
          <div>{`All Time (Since ${minDateYear})`}</div>
        </MenuItem>
      </Select>
    </FormControl>
  ) : null;
}

export default React.memo(RegisterOnAfterDate);
